// Certification Gates panel.
//
// Per-tab progress meter on the Overview tab. Rows for Underwriting +
// 3 analyst tabs + Closing. Each row deep-links to the corresponding tab.
// An analyst tab whose lane is Not Applicable reads "N/A", not "0 / 3"
// (staging run, 2026-10-14).

import { Lock, Minus } from "lucide-react";
import { Card } from "@/design-system/components/ui";
import type { TabProgress } from "@/api/dealFlow/types";

const TAB_SLUG: Record<string, string> = {
  Underwriting: "underwriting",
  FinancialAnalysis: "financial-analysis",
  LegalAnalysis: "legal-analysis",
  BusinessAnalysis: "business-analysis",
  Closing: "closing",
};

interface Props {
  dealId: string;
  tabs: TabProgress[];
  /** False on a Declined or Withdrawn deal, whose deal page does not open the
   *  tabs; the counts then show without a link. */
  linkToTabs?: boolean;
}

export function CertificationGatesPanel({ dealId, tabs, linkToTabs = true }: Props) {
  return (
    <Card className="p-space-4" aria-labelledby="certification-gates-heading">
      <h3
        id="certification-gates-heading"
        className="font-display text-h4 text-onyx uppercase tracking-wider mb-space-3"
      >
        Certification Gates
      </h3>
      <ul className="flex flex-col gap-space-2">
        {tabs.map((t) => {
          const pct = t.sectionsTotal > 0 ? (t.sectionsComplete / t.sectionsTotal) * 100 : 0;
          const slug = TAB_SLUG[t.tabCode] ?? "underwriting";
          const href = `/deal-flow/deals/${dealId}?tab=${slug}`;
          const summary = t.isLocked
            ? t.lockReason ?? "Locked"
            : `${t.sectionsComplete} of ${t.sectionsTotal} sections complete`;
          const notApplicable = !t.isLocked && t.isNotApplicable;

          return (
            <li key={t.tabCode} className="grid grid-cols-12 items-center gap-space-3">
              <span className="col-span-3 text-body text-onyx font-semibold">
                {t.tabDisplayName}
              </span>
              <span className="col-span-6">
                {t.isLocked ? (
                  <span
                    className="inline-flex items-center gap-space-1 text-small text-gray-mid"
                    aria-label={`Locked: ${t.lockReason ?? ""}`}
                  >
                    <Lock size={14} aria-hidden />
                    <span>{t.lockReason}</span>
                  </span>
                ) : notApplicable ? (
                  <span className="inline-flex items-center gap-space-1 text-small text-gray-mid">
                    <Minus size={14} aria-hidden />
                    <span>Lane marked Not Applicable</span>
                  </span>
                ) : (
                  <div
                    className="h-2 w-full bg-white-warm rounded-sm overflow-hidden"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={t.sectionsTotal}
                    aria-valuenow={t.sectionsComplete}
                    aria-label={`${t.tabDisplayName}: ${summary}`}
                  >
                    <div
                      className="h-full bg-gold"
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                )}
              </span>
              <span className="col-span-3 text-small text-gray-mid text-right">
                {t.isLocked ? (
                  <span>—</span>
                ) : !linkToTabs ? (
                  <span>{notApplicable ? "N/A" : `${t.sectionsComplete} / ${t.sectionsTotal}`}</span>
                ) : notApplicable ? (
                  <a className="text-gold underline" href={href}>
                    N/A →
                  </a>
                ) : (
                  <a className="text-gold underline" href={href}>
                    {t.sectionsComplete} / {t.sectionsTotal} →
                  </a>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
